import type { CaseMatterType, CourtOption } from './caseTaxonomyRegistry';

export interface CriminalProceeding extends CaseMatterType {
  criminalCategoryId?: string;
  recommendedDocuments?: string[];
  deprecated?: boolean;
  sideRoleKeys?: string[];
  sideOptionsByContext?: Record<string, string[]>;
}

export interface CriminalCategory {
  id: string;
  label: string;
  description: string;
  proceedings: CriminalProceeding[];
}

export interface CriminalTaxonomyConfig {
  id: string;
  label: string;
  description: string;
  active: boolean;
  categories: CriminalCategory[];
  types: CaseMatterType[];
}

export const CRIMINAL_COURTS = {
  sessions: {
    id: 'Court of Sessions',
    label: 'Court of Sessions',
    description: 'Court of Sessions.',
  },
  highCourt: {
    id: 'High Court',
    label: 'High Court',
    description: 'High Court.',
  },
  magistrate: {
    id: 'Magistrate Court',
    label: 'Magistrate Court',
    description: 'Judicial Magistrate.',
  },
  custom: {
    id: 'Other / Custom Forum',
    label: 'Other / Custom Forum',
    description: 'Use where a special statute or procedural context provides another route.',
  },
  sameCourtInterim: {
    id: 'Same Court Where Interim Bail Granted',
    label: 'Same Court Where Interim Bail Granted',
    description: 'Court that granted interim pre-arrest bail.',
  },
  sameCourt: {
    id: 'Same Court',
    label: 'Same Court',
    description: 'Same court seized of the bail matter.',
  },
  appellate: {
    id: 'Appellate Court',
    label: 'Appellate Court',
    description: 'Appellate court for suspension of sentence after conviction.',
  },
  special: {
    id: 'Special Court',
    label: 'Special Court',
    description: 'Special statute court.',
  },
  samePending: {
    id: 'Same Court Where Main Matter Is Pending',
    label: 'Same Court Where Main Matter Is Pending',
    description: 'Court where the connected criminal matter is pending.',
  },
  specialAppellate: {
    id: 'Special Appellate Forum',
    label: 'Special Appellate Forum',
    description: 'Special appellate forum under a special statute.',
  },
} satisfies Record<string, CourtOption>;

export const CRIMINAL_SIDES = {
  bail: ['Petitioner / Accused', 'State', 'Complainant'],
  stateTrial: ['State', 'Accused'],
  complaintTrial: ['Complainant', 'Accused'],
  appeal: ['Appellant', 'Respondent'],
  revision: ['Revisionist', 'Respondent'],
  hcCriminal: ['Petitioner', 'Respondents'],
  misc: ['Applicant', 'Respondent', 'State'],
  privateComplaint: ['Complainant', 'Accused / Respondent'],
  other: ['Party A', 'Party B', 'Accused', 'Complainant', 'State'],
} as const;

export const SPECIAL_COURT_SUBTYPES = [
  'Anti-Terrorism Court',
  'CNSA / Narcotics',
  'FIA / Cybercrime',
  'NAB',
  'Banking Court',
  'Customs',
  'Anti-Corruption',
  'Other',
] as const;

export function flattenCriminalProceedingsForRegistry(
  categories: CriminalCategory[],
): CaseMatterType[] {
  return categories.flatMap((category) =>
    category.proceedings.map((proceeding) => ({
      ...proceeding,
      criminalCategoryId: category.id,
    })),
  );
}

export function getProceedingsForCategory(
  config: CriminalTaxonomyConfig,
  categoryId: string,
): CriminalProceeding[] {
  return config.categories.find((c) => c.id === categoryId)?.proceedings ?? [];
}

export function getSelectableProceedingsForCategory(
  config: CriminalTaxonomyConfig,
  categoryId: string,
): CriminalProceeding[] {
  return getProceedingsForCategory(config, categoryId).filter((p) => !p.deprecated);
}

export function getCriminalProceeding(
  config: CriminalTaxonomyConfig,
  categoryId: string,
  proceedingId: string,
): CriminalProceeding | undefined {
  return getProceedingsForCategory(config, categoryId).find((p) => p.id === proceedingId);
}

export function findCriminalProceedingById(
  config: CriminalTaxonomyConfig,
  proceedingId: string,
): CriminalProceeding | undefined {
  const resolved = proceedingId?.trim();
  if (!resolved) return undefined;
  for (const category of config.categories) {
    const match = category.proceedings.find((p) => p.id === resolved);
    if (match) {
      return { ...match, criminalCategoryId: category.id };
    }
  }
  return config.types.find((t) => t.id === resolved) as CriminalProceeding | undefined;
}

export function resolveCriminalProceedingFromCase(
  config: CriminalTaxonomyConfig,
  caseData: {
    criminalCategory?: string;
    matterType?: string;
    caseType?: string;
    proceedingType?: string;
  },
): CriminalProceeding | undefined {
  const proceedingId = caseData.proceedingType || caseData.matterType || caseData.caseType || '';
  const categoryId = caseData.criminalCategory || '';

  if (categoryId && proceedingId) {
    const direct = getCriminalProceeding(config, categoryId, proceedingId);
    if (direct) return direct;
  }

  return findCriminalProceedingById(config, proceedingId);
}

export function getCriminalPartyLabels(
  proceeding: CriminalProceeding | undefined,
  trialContext?: 'state' | 'complaint',
): {
  party1Label: string;
  party2Label: string;
  party1Plural: string;
  party2Plural: string;
  sideRoleKeys: [string, string];
} {
  const defaultLabels = {
    party1Label: 'Party A',
    party2Label: 'Party B',
    party1Plural: 'Parties A',
    party2Plural: 'Parties B',
    sideRoleKeys: ['party_a', 'party_b'] as [string, string],
  };
  if (!proceeding?.sideOptions?.length) return defaultLabels;

  let sides = proceeding.sideOptions;
  if (proceeding.sideOptionsByContext && trialContext) {
    sides =
      proceeding.sideOptionsByContext[trialContext] ||
      proceeding.sideOptionsByContext.state ||
      sides;
  }

  const p1 = sides[0];
  const p2 =
    sides.includes('State') && sides.length >= 3 ? 'State / Complainant' : sides[1] || 'Party B';
  const roleKeys = proceeding.sideRoleKeys || ['party_a', 'party_b'];

  const pluralize = (label: string) => {
    if (label.includes('/')) return `${label}s`;
    if (label.endsWith('s')) return label;
    return `${label}s`;
  };

  return {
    party1Label: p1,
    party2Label: p2,
    party1Plural: pluralize(p1),
    party2Plural: pluralize(p2),
    sideRoleKeys: [roleKeys[0] || 'party_a', roleKeys[1] || 'party_b'],
  };
}

export function criminalSideRoleToRepresentingSide(role: string): 'plaintiff' | 'defendant' {
  const defendantRoles = ['state', 'respondent'];
  return defendantRoles.includes(role.toLowerCase()) ? 'defendant' : 'plaintiff';
}

export function resolveCriminalStartingStage(
  proceeding: CriminalProceeding | undefined,
  representingSideRole?: string,
): string {
  const stages = proceeding?.defaultStages ?? [];
  if (stages.length === 0) return 'Case created';

  const role = (representingSideRole || '').toLowerCase();
  const isOppositionSide = role === 'state' || role === 'respondent';

  if (isOppositionSide) {
    return (
      stages.find((s) =>
        /notice|reply|objection|challan|report|comments|parawise/i.test(s),
      ) ??
      stages[Math.min(3, stages.length - 1)] ??
      stages[0]
    );
  }
  return stages[0];
}

export const LEGACY_CRIMINAL_TYPE_MAP: Record<
  string,
  { categoryId: string; proceedingId: string }
> = {
  Bail: { categoryId: 'Bail Matters', proceedingId: 'Post-Arrest Bail (BA)' },
  'Bail Application': { categoryId: 'Bail Matters', proceedingId: 'Post-Arrest Bail (BA)' },
  'Protective Bail': { categoryId: 'Bail Matters', proceedingId: 'Protective / Transit Bail' },
  'Criminal Trial': { categoryId: 'Criminal Trial', proceedingId: 'Sessions Trial' },
  'Criminal Appeal': { categoryId: 'Criminal Appeal', proceedingId: 'Appeal Against Conviction' },
  'Criminal Revision': { categoryId: 'Criminal Revision', proceedingId: 'Revision Against Order' },
  'Criminal Miscellaneous Application': {
    categoryId: 'Criminal Miscellaneous',
    proceedingId: 'Other Criminal Application',
  },
  'Private Complaint': { categoryId: 'Private Complaint', proceedingId: 'PPC Complaint' },
  'Quashment Petition': {
    categoryId: 'High Court Criminal Jurisdiction',
    proceedingId: 'Quashment of FIR',
  },
  'Other Criminal Matter': {
    categoryId: 'Other Criminal Matter',
    proceedingId: 'Custom Criminal Matter',
  },
};

export const LEGACY_CRIMINAL_SUBTYPE_MAP: Record<
  string,
  { categoryId: string; proceedingId: string }
> = {
  'pre-arrest bail': { categoryId: 'Bail Matters', proceedingId: 'Bail Before Arrest (BBA)' },
  'bail before arrest': { categoryId: 'Bail Matters', proceedingId: 'Bail Before Arrest (BBA)' },
  'post-arrest bail': { categoryId: 'Bail Matters', proceedingId: 'Post-Arrest Bail (BA)' },
  'bail after arrest': { categoryId: 'Bail Matters', proceedingId: 'Post-Arrest Bail (BA)' },
  'protective bail': { categoryId: 'Bail Matters', proceedingId: 'Protective / Transit Bail' },
  'transit protective bail': { categoryId: 'Bail Matters', proceedingId: 'Protective / Transit Bail' },
  'bail cancellation': { categoryId: 'Bail Matters', proceedingId: 'Bail Cancellation' },
  'bail confirmation': { categoryId: 'Bail Matters', proceedingId: 'Bail Confirmation' },
  'bail after conviction': {
    categoryId: 'Bail Matters',
    proceedingId: 'Bail After Conviction / Suspension of Sentence',
  },
  'police challan case': { categoryId: 'Criminal Trial', proceedingId: 'Police Challan / State Case' },
  'state case': { categoryId: 'Criminal Trial', proceedingId: 'Police Challan / State Case' },
  'complaint case': { categoryId: 'Criminal Trial', proceedingId: 'Complaint Case' },
  'complaint case trial': { categoryId: 'Criminal Trial', proceedingId: 'Complaint Case' },
  'sessions trial': { categoryId: 'Criminal Trial', proceedingId: 'Sessions Trial' },
  'magistrate trial': { categoryId: 'Criminal Trial', proceedingId: 'Magistrate Trial' },
  'special court trial': { categoryId: 'Criminal Trial', proceedingId: 'Special Court Trial' },
  'appeal against conviction': {
    categoryId: 'Criminal Appeal',
    proceedingId: 'Appeal Against Conviction',
  },
  'appeal against acquittal': {
    categoryId: 'Criminal Appeal',
    proceedingId: 'Appeal Against Acquittal',
  },
  'appeal against sentence': { categoryId: 'Criminal Appeal', proceedingId: 'Appeal Against Sentence' },
  'jail appeal': { categoryId: 'Criminal Appeal', proceedingId: 'Jail Appeal' },
  'appeal against order': { categoryId: 'Criminal Appeal', proceedingId: 'Appeal Against Order' },
  'revision against order': { categoryId: 'Criminal Revision', proceedingId: 'Revision Against Order' },
  superdari: { categoryId: 'Criminal Miscellaneous', proceedingId: 'Superdari' },
  'application for superdari': { categoryId: 'Criminal Miscellaneous', proceedingId: 'Superdari' },
  'quashing of fir': {
    categoryId: 'High Court Criminal Jurisdiction',
    proceedingId: 'Quashment of FIR',
  },
  'quashing of criminal proceedings': {
    categoryId: 'High Court Criminal Jurisdiction',
    proceedingId: 'Quashment of Proceedings',
  },
  'quashing of complaint': {
    categoryId: 'High Court Criminal Jurisdiction',
    proceedingId: 'Quashment of Complaint',
  },
};
